import React, { useState, useEffect } from "react";
import { Row, Button, Card, Col } from "react-bootstrap";
import FieldGroup from "./ExperienceInputs/FieldGroup";
import { FaPlus } from "react-icons/fa";
import { CloudWatchLogs } from "aws-sdk";
import MultipleFileUpload from "./MultipleFileUpload";
import { camelCase } from "change-case";

const ExperienceInputs = ({
  setApplicationDetails,
  applicationDetails,
  postandvacancyDetails,
  style,
  applicationID,
  advertisementClosingDate,
  application,
  applicationForm,
}) => {
  const [formData, setFormData] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  // IDs (as provided)
  const GRADUATION_YEAR_ID = "66a687874db5f02d6861ed0d";
  const GRADUATION_COURSE_ID = "66a687874db5f02d6861ed09"; // graduation course name id (provided)
  const POST_GRAD_SECTION_ID = "676a57139e167618b7ec04a1";
  const POST_GRAD_YEAR_ID = "676a57139e167618b7ec04a7";
  const LLB_SECTION_ID = "68ecd888eb422bea55aa71a4"; // section presence for LLB/legal degree
  const LLB_YEAR_ID = "68ecd888eb422bea55aa71aa"; // year-of-passing field id for LLB

  // Check if a qualification field exists in the current job form
  const isFieldInCurrentForm = (fieldId) => {
    if (!application?.inputElements && !applicationForm) return false;

    // Check in application.inputElements (flat structure)
    if (application?.inputElements) {
      const fieldExists = application.inputElements.some(
        (element) => element._id === fieldId
      );
      if (fieldExists) return true;
    }

    // Check in applicationForm (grouped by tabs)
    if (applicationForm && Array.isArray(applicationForm)) {
      for (const tab of applicationForm) {
        if (tab?.inputElements && Array.isArray(tab.inputElements)) {
          const fieldExists = tab.inputElements.some(
            (element) => element?.values?._id === fieldId
          );
          if (fieldExists) return true;
        }
      }
    }

    return false;
  };

  // Get the highest year of passing from Essential Qualifications (Graduation, Post-Graduation, LLB)
  // ONLY from qualifications that are present in the CURRENT job form
  // This determines the minimum "From Year" for Experience section
  const getHighestEssentialQualificationYear = () => {
    if (!applicationDetails) return null;

    const qualificationYears = [];

    // Helper function to validate and extract year from a value
    const extractValidYear = (yearValue, fieldId = "") => {
      if (!yearValue) return null;

      // Convert to string first to handle both string and number inputs
      const yearStr = String(yearValue).trim();

      // Check if it's a valid 4-digit year (between 1900 and 2100)
      if (yearStr.length === 4 && /^\d{4}$/.test(yearStr)) {
        const year = parseInt(yearStr, 10);
        if (year >= 1900 && year <= 2100) {
          return year;
        }
      }

      return null;
    };

    // Graduation Year of Passing - always check (it's always required)
    const gradYearRaw1 = applicationDetails?.[GRADUATION_YEAR_ID];
    const gradYearRaw2 = applicationDetails?.[camelCase(GRADUATION_YEAR_ID)];

    const gradYear1 = extractValidYear(gradYearRaw1, GRADUATION_YEAR_ID);
    const gradYear2 = extractValidYear(gradYearRaw2, GRADUATION_YEAR_ID);

    if (gradYear1) {
      qualificationYears.push(gradYear1);
    }
    if (gradYear2 && gradYear2 !== gradYear1) {
      qualificationYears.push(gradYear2);
    }

    // Post-Graduation Year of Passing - ONLY if Post-Graduation fields exist in current form
    const POST_GRAD_YEAR_ID_1 = "676a57139e167618b7ec04ac";
    const POST_GRAD_YEAR_ID_2 = "676a57139e167618b7ec04a7";

    // Check if Post-Graduation section exists in current form
    const hasPostGradInForm =
      isFieldInCurrentForm(POST_GRAD_YEAR_ID_1) ||
      isFieldInCurrentForm(POST_GRAD_YEAR_ID_2) ||
      isFieldInCurrentForm(POST_GRAD_SECTION_ID);

    if (hasPostGradInForm) {
      const postGradYearRaw1a = applicationDetails?.[POST_GRAD_YEAR_ID_1];
      const postGradYearRaw1b =
        applicationDetails?.[camelCase(POST_GRAD_YEAR_ID_1)];
      const postGradYearRaw2a = applicationDetails?.[POST_GRAD_YEAR_ID_2];
      const postGradYearRaw2b =
        applicationDetails?.[camelCase(POST_GRAD_YEAR_ID_2)];

      const postGradYear1a = extractValidYear(
        postGradYearRaw1a,
        POST_GRAD_YEAR_ID_1
      );
      const postGradYear1b = extractValidYear(
        postGradYearRaw1b,
        POST_GRAD_YEAR_ID_1
      );
      const postGradYear2a = extractValidYear(
        postGradYearRaw2a,
        POST_GRAD_YEAR_ID_2
      );
      const postGradYear2b = extractValidYear(
        postGradYearRaw2b,
        POST_GRAD_YEAR_ID_2
      );

      if (postGradYear1a) qualificationYears.push(postGradYear1a);
      if (postGradYear1b && postGradYear1b !== postGradYear1a)
        qualificationYears.push(postGradYear1b);
      if (postGradYear2a) qualificationYears.push(postGradYear2a);
      if (postGradYear2b && postGradYear2b !== postGradYear2a)
        qualificationYears.push(postGradYear2b);
    }

    // LLB Year of Passing - ONLY if LLB fields exist in current form
    const LLB_YEAR_ID_1 = "68ecd888eb422bea55aa71ad";
    const LLB_YEAR_ID_2 = "68ecd888eb422bea55aa71aa";

    // Check if LLB section exists in current form
    const hasLLBInForm =
      isFieldInCurrentForm(LLB_YEAR_ID_1) ||
      isFieldInCurrentForm(LLB_YEAR_ID_2) ||
      isFieldInCurrentForm(LLB_SECTION_ID);

    if (hasLLBInForm) {
      const llbYearRaw1a = applicationDetails?.[LLB_YEAR_ID_1];
      const llbYearRaw1b = applicationDetails?.[camelCase(LLB_YEAR_ID_1)];
      const llbYearRaw2a = applicationDetails?.[LLB_YEAR_ID_2];
      const llbYearRaw2b = applicationDetails?.[camelCase(LLB_YEAR_ID_2)];

      const llbYear1a = extractValidYear(llbYearRaw1a, LLB_YEAR_ID_1);
      const llbYear1b = extractValidYear(llbYearRaw1b, LLB_YEAR_ID_1);
      const llbYear2a = extractValidYear(llbYearRaw2a, LLB_YEAR_ID_2);
      const llbYear2b = extractValidYear(llbYearRaw2b, LLB_YEAR_ID_2);

      if (llbYear1a) qualificationYears.push(llbYear1a);
      if (llbYear1b && llbYear1b !== llbYear1a)
        qualificationYears.push(llbYear1b);
      if (llbYear2a) qualificationYears.push(llbYear2a);
      if (llbYear2b && llbYear2b !== llbYear2a)
        qualificationYears.push(llbYear2b);
    }

    // Debug logging to help identify the issue
    console.log("=== Experience Year Validation Debug ===");
    console.log("Has Post-Grad in form:", hasPostGradInForm);
    console.log("Has LLB in form:", hasLLBInForm);
    console.log("Graduation Year:", gradYear1 || gradYear2);
    console.log(
      "All qualification years found (from current form only):",
      qualificationYears
    );

    if (qualificationYears.length > 0) {
      const highestYear = Math.max(...qualificationYears);
      console.log("Highest year calculated:", highestYear);
      return highestYear;
    }

    console.log("No valid qualification years found");
    return null;
  };

  // Determine which education year should determine the experience start date.
  // For Legal vacancies:
  //   - If graduation course == "Integrated LLB" => use graduation year.
  //   - Else (not integrated LLB) => use LLB year (if present), otherwise fallback to graduation.
  // For non-Legal vacancies:
  //   - Prefer post-graduation year (if present) then graduation year.
  const getEducationYearForExperience = () => {
    if (!applicationDetails) return null;

    const deptName =
      applicationDetails?.vacancyId?.department?.name?.trim()?.toLowerCase() ||
      "";
    const isLegalDept = deptName === "legal";

    // Read graduation course (string), if present
    const gradCourseRaw = applicationDetails?.[GRADUATION_COURSE_ID];
    const gradCourse =
      typeof gradCourseRaw === "string"
        ? gradCourseRaw.trim().toLowerCase()
        : "";

    if (isLegalDept) {
      // If candidate selected integrated LLB as graduation course -> use graduation year
      const isIntegratedLLB =
        gradCourse &&
        (gradCourse === "integrated llb" ||
          (gradCourse.includes("integrated") && gradCourse.includes("llb")));

      const gradYearRaw = applicationDetails?.[GRADUATION_YEAR_ID];
      const gradYear = Number(gradYearRaw);

      if (isIntegratedLLB) {
        if (gradYear && gradYear > 0) return gradYear;
        // if graduation year missing, fall through to other checks (LLB or post-grad) as best-effort
      } else {
        // Not integrated-LLB: prefer LLB year (if provided)
        const llbYearRaw = applicationDetails?.[LLB_YEAR_ID];
        const llbYear = Number(llbYearRaw);
        if (llbYear && llbYear > 0) return llbYear;

        // If LLB not present or invalid, fallback to graduation year
        if (gradYear && gradYear > 0) return gradYear;
      }

      // As last resort, consider post-grad if available (unlikely for Legal precedence but safe)
      const postGradYearRaw = applicationDetails?.[POST_GRAD_YEAR_ID];
      const postGradYear = Number(postGradYearRaw);
      if (postGradYear && postGradYear > 0) return postGradYear;

      return null;
    }

    // Non-legal: prefer post-graduation year then graduation
    const postGradYearRaw = applicationDetails?.[POST_GRAD_YEAR_ID];
    const postGradYear = Number(postGradYearRaw);
    if (postGradYear && postGradYear > 0) return postGradYear;

    const gradYearRaw = applicationDetails?.[GRADUATION_YEAR_ID];
    const gradYear = Number(gradYearRaw);
    if (gradYear && gradYear > 0) return gradYear;

    return null;
  };

  const getMinExperienceDateFromGraduation = () => {
    // Use the highest year from Essential Qualifications (Graduation, Post-Graduation, LLB)
    const highestYear = getHighestEssentialQualificationYear();
    if (highestYear && highestYear > 0) {
      // Experience "From Year" must be the highest passing year or later
      // So minimum date is January 1st of the highest passing year
      return `${highestYear}-01-01`;
    }
    return null;
  };

  // UTC-safe helpers to avoid timezone drift
  const toUTCDate = (ymd) => {
    if (!ymd) return null;
    const [y, m, d] = ymd.split("-").map(Number);
    return new Date(Date.UTC(y, (m || 1) - 1, d || 1));
  };

  const inclusiveDaysBetweenUTC = (startYmd, endYmd) => {
    const start = toUTCDate(startYmd);
    const end = toUTCDate(endYmd);
    if (!start || !end) return 0;
    const millis = end.getTime() - start.getTime();
    return Math.floor(millis / (1000 * 60 * 60 * 24)) + 1;
  };

  // Helper function to normalize days to months using actual calendar month lengths
  // Handles 28, 29 (leap year), 30, 31 day months correctly
  const normalizeDaysToMonthsCalendarWise = (
    years,
    months,
    days,
    referenceYear,
    referenceMonth
  ) => {
    if (days === 0) return { years, months, days: 0 };

    let currentYear = referenceYear || 2000;
    let currentMonth = referenceMonth !== undefined ? referenceMonth : 0;
    let remainingDays = days;
    let additionalMonths = 0;

    // Convert excess days to months using actual calendar month lengths
    // Progressively check if we can form complete calendar months
    while (remainingDays > 0) {
      // Get days in the current month (handles leap years automatically via Date API)
      const daysInCurrentMonth = new Date(
        Date.UTC(currentYear, currentMonth + 1, 0)
      ).getUTCDate();

      if (remainingDays >= daysInCurrentMonth) {
        // Can convert a full calendar month
        remainingDays -= daysInCurrentMonth;
        additionalMonths += 1;
        currentMonth += 1;

        // Handle month overflow to next year
        if (currentMonth >= 12) {
          currentMonth = 0;
          currentYear += 1;
        }
      } else {
        // Remaining days are less than a full month, stop here
        break;
      }
    }

    // Add the additional months we calculated
    months += additionalMonths;

    // Handle months overflow to years
    if (months >= 12) {
      years += Math.floor(months / 12);
      months = months % 12;
    }

    return { years, months, days: remainingDays };
  };

  // Convert total inclusive days to years, months, days using month/date based calculation
  const ymdFromTotalDaysUTC = (totalDays) => {
    if (totalDays <= 0) return { years: 0, months: 0, days: 0 };

    // Start from a base date and add totalDays (already inclusive)
    const baseDate = new Date(Date.UTC(1900, 0, 1));
    // totalDays already includes both start and end dates (inclusive)
    // So if we have 31 days, that means 30 days after the start date
    const endDate = new Date(
      baseDate.getTime() + (totalDays - 1) * 24 * 60 * 60 * 1000
    );

    // Calculate difference in years, months, days
    let years = endDate.getUTCFullYear() - baseDate.getUTCFullYear();
    let months = endDate.getUTCMonth() - baseDate.getUTCMonth();
    let days = endDate.getUTCDate() - baseDate.getUTCDate() + 1; // +1 for inclusive

    // Handle negative days by borrowing from months
    if (days < 0) {
      months -= 1;
      const prevMonth = new Date(
        Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), 0)
      );
      days += prevMonth.getUTCDate();
    }

    // Handle overflow: if days equals or exceeds the end month's days, convert to months
    const daysInEndMonth = new Date(
      Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth() + 1, 0)
    ).getUTCDate();

    if (
      years === 0 &&
      months === 0 &&
      baseDate.getUTCMonth() === endDate.getUTCMonth()
    ) {
      if (
        baseDate.getUTCDate() === 1 &&
        endDate.getUTCDate() === daysInEndMonth
      ) {
        // Spans full month
        days = 0;
        months = 1;
      } else if (days >= daysInEndMonth) {
        // Days overflow, convert excess to months
        const excess = days - daysInEndMonth;
        days = excess;
        months = 1;
      }
    } else if (days > daysInEndMonth) {
      // Cross-month overflow
      const excess = days - daysInEndMonth;
      days = excess;
      months += 1;
    }

    // Handle negative months by borrowing from years
    if (months < 0) {
      years -= 1;
      months += 12;
    }

    // Handle overflow months
    if (months >= 12) {
      years += Math.floor(months / 12);
      months = months % 12;
    }

    // Calendar-wise normalization using actual month lengths (28, 29, 30, 31 days)
    // This handles leap years and different month lengths correctly
    return normalizeDaysToMonthsCalendarWise(
      years,
      months,
      days,
      endDate.getUTCFullYear(),
      endDate.getUTCMonth()
    );
  };

  // Inclusive exact-day experience calculator (UTC to avoid DST issues)
  // Always counts the last day (inclusive) and uses month/date based calculation
  const calculateTotalExperience = (startDate, endDate) => {
    if (!startDate || !endDate) return "";

    // Parse dates and ensure UTC to avoid timezone issues
    const start = toUTCDate(startDate);
    const end = toUTCDate(endDate);
    if (!start || !end || start > end) return "Invalid date range";

    // For inclusive calculation, we calculate the difference including both start and end dates
    // Example: Oct 1 to Oct 31 = 1 month, 0 days (spans full month)

    // Calculate years, months, days
    let years = end.getUTCFullYear() - start.getUTCFullYear();
    let months = end.getUTCMonth() - start.getUTCMonth();
    let days = end.getUTCDate() - start.getUTCDate() + 1; // +1 for inclusive (both dates count)

    // Handle negative days by borrowing from months
    if (days < 0) {
      months -= 1;
      const previousMonth = new Date(
        Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 0)
      );
      days += previousMonth.getUTCDate();
    }

    // Handle overflow: if days equals or exceeds the end month's days, convert to months
    const daysInEndMonth = new Date(
      Date.UTC(end.getUTCFullYear(), end.getUTCMonth() + 1, 0)
    ).getUTCDate();

    // Special case: if we're in the same month and days spans the full month, it's 1 month, 0 days
    if (
      years === 0 &&
      months === 0 &&
      start.getUTCMonth() === end.getUTCMonth()
    ) {
      if (start.getUTCDate() === 1 && end.getUTCDate() === daysInEndMonth) {
        // Spans full month
        days = 0;
        months = 1;
      } else if (days >= daysInEndMonth) {
        // Days overflow, convert excess to months
        const excess = days - daysInEndMonth;
        days = excess;
        months = 1;
      }
    } else if (days > daysInEndMonth) {
      // Cross-month overflow
      const excess = days - daysInEndMonth;
      days = excess;
      months += 1;
    }

    // Handle negative months by borrowing from years
    if (months < 0) {
      years -= 1;
      months += 12;
    }

    // Handle overflow months
    if (months >= 12) {
      years += Math.floor(months / 12);
      months = months % 12;
    }

    // Calendar-wise normalization using actual month lengths (28, 29, 30, 31 days)
    // This handles leap years and different month lengths correctly
    const normalized = normalizeDaysToMonthsCalendarWise(
      years,
      months,
      days,
      end.getUTCFullYear(),
      end.getUTCMonth()
    );

    return `${normalized.years} years, ${normalized.months} months, ${normalized.days} days`;
  };

  const calculateTotalExperienceSumWithoutTrainee = (entries) => {
    if (!entries || entries.length === 0) {
      return "0 years, 0 months, 0 days";
    }

    const nonTrainee = (entries || []).filter(
      (e) => e.isTrainee !== "Yes" && e.startDateOfService && e.endDateOfService
    );

    if (nonTrainee.length === 0) return "0 years, 0 months, 0 days";

    // Use the same logic as sumTotalExperiences for consistency
    return sumTotalExperiences(nonTrainee);
  };

  const calculateTotalExperienceSum = (entries) => {
    // Use the same logic as sumTotalExperiences for consistency
    // This ensures all calculations use month/date based logic and always count the last day
    if (!entries || entries.length === 0) {
      return "0 years, 0 months, 0 days";
    }

    // Filter valid entries
    const valid = (entries || []).filter(
      (e) => e.startDateOfService && e.endDateOfService
    );

    if (valid.length === 0) return "0 years, 0 months, 0 days";

    // Use sumTotalExperiences which handles consecutive segments and proper month/date calculation
    return sumTotalExperiences(valid);
  };

  const handleInputChange = (
    outerIndex,
    innerIndex,
    field,
    value,
    relevantIndex = null
  ) => {
    setFormData((prevData) => {
      const updatedData = [...prevData];
      if (outerIndex !== null && updatedData[outerIndex]) {
        if (innerIndex === null && relevantIndex === null) {
          updatedData[outerIndex] = {
            ...updatedData[outerIndex],
            [field]: value,
          };
        } else if (innerIndex !== null) {
          updatedData[outerIndex] = {
            ...updatedData[outerIndex],
            postWiseDetails: [
              ...(updatedData[outerIndex].postWiseDetails || []),
            ],
          };
          updatedData[outerIndex].postWiseDetails[innerIndex] = {
            ...updatedData[outerIndex].postWiseDetails[innerIndex],
            [field]: value,
          };
        } else if (relevantIndex !== null) {
          updatedData[outerIndex] = {
            ...updatedData[outerIndex],
            relevantExperience: [
              ...(updatedData[outerIndex].relevantExperience || []),
            ],
          };
          updatedData[outerIndex].relevantExperience[relevantIndex] = {
            ...updatedData[outerIndex].relevantExperience[relevantIndex],
            [field]: value,
          };
        }
      }
      return updatedData;
    });
  };

  const addFormEntry = (outerIndex) => {
    setFormData((prevData) => {
      const updatedData = [...prevData];

      if (!updatedData[outerIndex].postWiseDetails) {
        updatedData[outerIndex].postWiseDetails = [];
      }

      updatedData[outerIndex].postWiseDetails.push({
        isTrainee: "",
        designation: "",
        startDateOfService: "",
        endDateOfService: "",
      });

      return updatedData;
    });
  };

  const addRelevantExperience = (outerIndex) => {
    setFormData((prevData) => {
      const updatedData = [...prevData];

      if (!updatedData[outerIndex].relevantExperience) {
        updatedData[outerIndex].relevantExperience = [];
      }

      updatedData[outerIndex].relevantExperience.push({
        isTrainee: "",
        startDateOfService: "",
        endDateOfService: "",
        TotalExperience: "",
      });

      return updatedData;
    });
  };

  const deleteRelevantExperience = (outerIndex, innerIndex) => {
    setFormData((prevData) => {
      const updatedData = [...prevData];
      updatedData[outerIndex].relevantExperience = updatedData[
        outerIndex
      ].relevantExperience.filter((_, i) => i !== innerIndex);
      return updatedData;
    });
  };
  const deleteFormEntry = (outerIndex, innerIndex) => {
    setFormData((prevData) => {
      const updatedData = [...prevData];
      updatedData[outerIndex].postWiseDetails = updatedData[
        outerIndex
      ].postWiseDetails.filter((_, i) => i !== innerIndex);

      return updatedData;
    });
  };

  const addNewFieldGroup = () => {
    setFormData((prevState) => {
      let minStartDate = "";

      // Use the computed min date (legal/postgrad/grad logic applied in getEducationYearForExperience)
      const graduationMinDate = getMinExperienceDateFromGraduation();
      if (graduationMinDate) {
        minStartDate = graduationMinDate;
      } else if (prevState.length > 0) {
        const lastEndDate = prevState[prevState.length - 1].endDateOfService;
        if (lastEndDate) {
          const nextDay = new Date(lastEndDate);
          nextDay.setDate(nextDay.getDate() + 1);
          minStartDate = nextDay.toISOString().split("T")[0];
        }
      }

      return [
        ...prevState,
        {
          nameOfOrganization: "",
          typeOfOrganization: "",
          statusOfService: "",
          typeOfService: "",
          natureOfBusiness: "",
          natureOfBusinessOther: "",
          startDateOfService: "",
          endDateOfService: "",
          payScale: "",
          basicPay: "",
          ctc: "",
          totalExperience: "",
          proofOfExperienceDocument: [],
          postWiseDetails: [],
          relevantExperience: [],
          advertisementClosingDate: advertisementClosingDate,

          minStartDateOfService: minStartDate, // Pass this for min in FieldGroup
        },
      ];
    });
  };

  const deleteFieldGroup = (outerIndex) => {
    const updatedData = formData.filter((_, i) => i !== outerIndex);
    setFormData(updatedData);
  };

  const checkErrorFn = (
    fieldKey,
    immediateValue,
    outerIndex = null,
    relevantIndex = null
  ) => {
    let errCount = 0;

    // Check if this is basicPay field for Legal department
    const isBasicPayForLegal =
      fieldKey.includes("basicPay") &&
      applicationDetails?.vacancyId?.department?.name === "Legal";

    // Check if this is natureOfBusinessOther field (non-mandatory)
    const isNatureOfBusinessOther = fieldKey.includes("natureOfBusinessOther");

    // Required field validation - skip for basicPay in Legal department and natureOfBusinessOther
    if (!immediateValue && !isBasicPayForLegal && !isNatureOfBusinessOther) {
      errCount++;
      console.log({ fieldErrors });

      setFieldErrors((prevState) => ({
        ...prevState,
        [fieldKey]: {
          errors: ["This field is required."],
          touched: true,
        },
      }));
    } else {
      console.log({ fieldErrors });
      setFieldErrors((prevState) => ({
        ...prevState,
        [fieldKey]: { errors: [], touched: true },
      }));
    }

    // Specific validation for `relevantExperience` fields
    if (fieldKey.includes("startDateOfService") && relevantIndex !== null) {
      // Additional validation if needed for `startDateOfService`
    }

    if (fieldKey.includes("endDateOfService") && relevantIndex !== null) {
      // Additional validation if needed for `endDateOfService`
    }

    return errCount === 0; // Returns true if no errors, false otherwise
  };

  useEffect(() => {
    setApplicationDetails((prev) => ({
      ...prev,
      new_staticExperience: formData,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  // Expose validation function to parent component for Save & Next
  useEffect(() => {
    // Store validation function in window for checkErrorsClass to access
    // This ensures natureOfBusiness and other fields are validated when Save & Next is clicked
    window.validateExperienceFields = () => {
      formData.forEach((data, outerIndex) => {
        // Validate natureOfBusiness if not Legal department
        const isLegalDept =
          applicationDetails?.vacancyId?.department?.name
            ?.trim()
            ?.toLowerCase() === "legal";
        if (!isLegalDept) {
          checkErrorFn(
            `natureOfBusiness${outerIndex}`,
            data.natureOfBusiness || ""
          );
        }
      });
    };
    return () => {
      delete window.validateExperienceFields;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, applicationDetails]);
  useEffect(() => {
    if (applicationDetails?.newStaticExperience?.length > 0) {
      setFormData(applicationDetails.newStaticExperience);
    }
  }, [applicationDetails?.newStaticExperience]);

  // Reset isRelevantChecked when post doesn't require relevant experience
  useEffect(() => {
    if (
      postandvacancyDetails &&
      !postandvacancyDetails?.relevantExperiences?.[0]?.experience
    ) {
      setFormData((prevData) => {
        return prevData.map((data) => ({
          ...data,
          isRelevantChecked: false,
          relevantExperience: [], // Also clear relevant experience data
        }));
      });
    }
  }, [postandvacancyDetails]);

  // Function to get maximum essential qualification year
  const getMaxEssentialQualificationYear = () => {
    if (!applicationDetails) return null;

    const qualificationYears = [];

    // 10th Grade Year of Passing
    const year10thId = "66a687874db5f02d6861ed02";
    const year10th = applicationDetails[year10thId];
    if (year10th && year10th.length === 4) {
      qualificationYears.push(parseInt(year10th));
    }

    // 12th Grade Year of Passing
    const year12thId = "66a727079680c25e280d6b2d";
    const year12th = applicationDetails[year12thId];
    if (year12th && year12th.length === 4) {
      qualificationYears.push(parseInt(year12th));
    }

    // Graduation Year of Passing
    const yearGraduationId = "66a687874db5f02d6861ed0d";
    const yearGraduation = applicationDetails[yearGraduationId];
    if (yearGraduation && yearGraduation.length === 4) {
      qualificationYears.push(parseInt(yearGraduation));
    }

    // Post Graduation Year of Passing
    const yearPostGraduationId = "676a57139e167618b7ec04ac";
    const yearPostGraduation = applicationDetails[yearPostGraduationId];
    if (yearPostGraduation && yearPostGraduation.length === 4) {
      qualificationYears.push(parseInt(yearPostGraduation));
    }

    // LLB Year of Passing
    const yearLLBId = "68ecd888eb422bea55aa71ad";
    const yearLLB = applicationDetails[yearLLBId];
    if (yearLLB && yearLLB.length === 4) {
      qualificationYears.push(parseInt(yearLLB));
    }

    // Return the maximum year, or null if no valid years found
    return qualificationYears.length > 0
      ? Math.max(...qualificationYears)
      : null;
  };

  const parseExperienceString = (experienceStr) => {
    if (!experienceStr || typeof experienceStr !== "string") {
      console.warn(`Invalid experience string: "${experienceStr}"`);
      return { years: 0, months: 0, days: 0 };
    }

    const matches = experienceStr.match(/\d+/g)?.map(Number) || [0, 0, 0];
    const [years = 0, months = 0, days = 0] = matches;

    return { years, months, days };
  };

  // Aggregate across organizations using consecutive segments approach for consistency
  // Always counts the last day (inclusive) - inclusiveDaysBetweenUTC already does this
  const sumTotalExperiences = (entries) => {
    if (!entries || entries.length === 0) return "0 years, 0 months, 0 days";
    const valid = (entries || []).filter(
      (e) => e.startDateOfService && e.endDateOfService
    );
    if (valid.length === 0) return "0 years, 0 months, 0 days";

    // Check if entries are consecutive (no gaps)
    let isConsecutive = true;
    const sortedEntries = [...valid].sort(
      (a, b) =>
        toUTCDate(a.startDateOfService) - toUTCDate(b.startDateOfService)
    );

    for (let i = 0; i < sortedEntries.length - 1; i++) {
      const currentEnd = toUTCDate(sortedEntries[i].endDateOfService);
      const nextStart = toUTCDate(sortedEntries[i + 1].startDateOfService);
      const nextDay = new Date(currentEnd);
      nextDay.setUTCDate(nextDay.getUTCDate() + 1);

      if (nextDay.getTime() !== nextStart.getTime()) {
        isConsecutive = false;
        break;
      }
    }

    // If consecutive, use overall date range
    if (isConsecutive && sortedEntries.length > 0) {
      const minStart = sortedEntries[0].startDateOfService;
      const maxEnd = sortedEntries[sortedEntries.length - 1].endDateOfService;
      return calculateTotalExperience(minStart, maxEnd);
    }

    // If not consecutive, sum individual inclusive days
    // Note: inclusiveDaysBetweenUTC already counts both start and end dates (+1)
    let totalDays = 0;
    valid.forEach((entry) => {
      totalDays += inclusiveDaysBetweenUTC(
        entry.startDateOfService,
        entry.endDateOfService
      );
    });

    if (totalDays === 0) return "0 years, 0 months, 0 days";

    // Convert total inclusive days to years, months, days
    const { years, months, days } = ymdFromTotalDaysUTC(totalDays);
    return `${years} years, ${months} months, ${days} days`;
  };

  // Helper function to sum multiple experience strings
  const sumExperienceStrings = (experienceStrings) => {
    if (!experienceStrings || experienceStrings.length === 0) {
      return "0 years, 0 months, 0 days";
    }

    // Filter valid strings first
    const validStrings = experienceStrings.filter(
      (expStr) =>
        expStr &&
        expStr !== "Invalid date range" &&
        expStr !== "0 years, 0 months, 0 days"
    );

    if (validStrings.length === 0) {
      return "0 years, 0 months, 0 days";
    }

    // If only one entry, return it directly to avoid conversion errors
    if (validStrings.length === 1) {
      return validStrings[0];
    }

    // Convert each experience string to total inclusive days, sum them, then convert back
    // This avoids normalization errors from summing years/months/days separately
    let totalDays = 0;

    validStrings.forEach((expStr) => {
      const matches = expStr.match(/\d+/g)?.map(Number) || [0, 0, 0];
      const [years, months, daysVal] = matches;

      // Convert "X years, Y months, Z days" to total inclusive days
      // The experience string represents the result of calculateTotalExperience(start, end)
      // where calculateTotalExperience does: end = end + 1, then calculates difference
      // So if we have "X years, Y months, Z days", the actual date range is:
      // start to (start + X years + Y months + Z days - 1)
      // The inclusive days = (end - start) + 1 = ((start + X years + Y months + Z days - 1) - start) + 1
      // = X years + Y months + Z days (in days)

      // Create a base date and construct what the end date would be before the +1
      const baseDate = new Date("1900-01-01");
      let endDate = new Date(baseDate);
      endDate.setFullYear(endDate.getFullYear() + years);
      endDate.setMonth(endDate.getMonth() + months);
      // The days value represents what the day component is after adding +1 to end
      // So original end date = base + years + months + days - 1
      endDate.setDate(endDate.getDate() + daysVal - 1);

      // Calculate inclusive days
      const daysDiff =
        Math.floor(
          (endDate.getTime() - baseDate.getTime()) / (1000 * 60 * 60 * 24)
        ) + 1;

      totalDays += daysDiff;
    });

    if (totalDays === 0) {
      return "0 years, 0 months, 0 days";
    }

    // Convert total days back using calculateTotalExperience logic
    const startDate = new Date("1900-01-01");
    // totalDays is inclusive days, so end date = start + totalDays - 1
    let endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + totalDays - 1);

    // Apply calculateTotalExperience logic: add +1, then calculate difference
    let tempEnd = new Date(endDate);
    tempEnd.setDate(tempEnd.getDate() + 1);

    let years = tempEnd.getFullYear() - startDate.getFullYear();
    let months = tempEnd.getMonth() - startDate.getMonth();
    let days = tempEnd.getDate() - startDate.getDate();

    if (days < 0) {
      months -= 1;
      const previousMonth = new Date(
        tempEnd.getFullYear(),
        tempEnd.getMonth(),
        0
      );
      days += previousMonth.getDate();
    }
    if (months < 0) {
      years -= 1;
      months += 12;
    }

    return `${years} years, ${months} months, ${days} days`;
  };

  // Combine consecutive or directly abutting segments (UTC-safe) into single segments
  function combineConsecutiveSegments(entries) {
    if (!entries || entries.length === 0) return [];
    // Filter entries with both start and end
    const valid = entries
      .filter((e) => e.startDateOfService && e.endDateOfService)
      .map((e) => ({ ...e }));
    if (valid.length === 0) return [];
    // Sort by startDate
    valid.sort(
      (a, b) =>
        toUTCDate(a.startDateOfService) - toUTCDate(b.startDateOfService)
    );
    // Prepare result
    const combined = [];
    let current = {
      start: valid[0].startDateOfService,
      end: valid[0].endDateOfService,
    };
    for (let i = 1; i < valid.length; i++) {
      const prevEnd = current.end;
      const currStart = valid[i].startDateOfService;
      // If consecutive (prevEnd +1 == currStart)
      const nextDay = new Date(toUTCDate(prevEnd));
      nextDay.setUTCDate(nextDay.getUTCDate() + 1);
      const isConsecutive = nextDay.toISOString().slice(0, 10) === currStart;
      if (isConsecutive) {
        // Extend current to this segment's end
        current.end = valid[i].endDateOfService;
      } else {
        combined.push({
          startDateOfService: current.start,
          endDateOfService: current.end,
        });
        current = {
          start: valid[i].startDateOfService,
          end: valid[i].endDateOfService,
        };
      }
    }
    combined.push({
      startDateOfService: current.start,
      endDateOfService: current.end,
    });
    return combined;
  }

  const [allExpDetails, setAllExpDetails] = useState([]);

  useEffect(() => {
    // Build per-organization summary (kept for UI/debug)
    let allExpSummery = [];
    for (let index = 0; index < formData.length; index++) {
      let experienceSummary = {};
      experienceSummary.index = index;
      const _formData = formData[index];
      if (_formData?.startDateOfService && _formData?.endDateOfService) {
        experienceSummary.baseTotal = calculateTotalExperience(
          _formData.startDateOfService,
          _formData.endDateOfService
        );
      }
      if (_formData?.postWiseDetails) {
        experienceSummary.postWiseTotalExperience = calculateTotalExperienceSum(
          _formData.postWiseDetails
        );
        experienceSummary.postWiseTotalExperienceWithoutTrainee =
          calculateTotalExperienceSumWithoutTrainee(_formData.postWiseDetails);
      }
      if (_formData?.relevantExperience) {
        experienceSummary.relevantTotalExperience = calculateTotalExperienceSum(
          _formData.relevantExperience
        );
        experienceSummary.relevantTotalExperienceWithoutTrainee =
          calculateTotalExperienceSumWithoutTrainee(
            _formData.relevantExperience
          );
      }
      allExpSummery.push(experienceSummary);
    }

    // Flatten across all organizations and compute totals from raw date ranges
    const allPosts = formData.flatMap((f) => f.postWiseDetails || []);
    const allPostsNonTrainee = allPosts.filter((e) => e.isTrainee !== "Yes");
    const allRelevantRaw = formData.flatMap((f) => f.relevantExperience || []);
    const allRelevant = combineConsecutiveSegments(allRelevantRaw);
    const allRelevantNonTrainee = combineConsecutiveSegments(
      allRelevantRaw.filter((e) => e.isTrainee !== "Yes")
    );
    const allOrgRanges = formData
      .filter((f) => f.startDateOfService && f.endDateOfService)
      .map((f) => ({
        startDateOfService: f.startDateOfService,
        endDateOfService: f.endDateOfService,
      }));

    let total_BaseExp = sumTotalExperiences(allOrgRanges);
    let total_postWiseTotalExperience = sumTotalExperiences(allPosts);
    // Excluding trainees: aggregate directly from flattened segments (UTC-safe)
    let total_postWiseTotalExperienceWithoutTrainee =
      sumTotalExperiences(allPostsNonTrainee);
    let total_relevantTotalExperience = sumTotalExperiences(allRelevant);
    let total_relevantTotalExperienceWithoutTrainee = sumTotalExperiences(
      allRelevantNonTrainee
    );

    let finalSummeryExp = {
      total_BaseExp,
      total_postWiseTotalExperience,
      total_postWiseTotalExperienceWithoutTrainee,
      total_relevantTotalExperience,
      total_relevantTotalExperienceWithoutTrainee,
      allExpSummery,
    };
    setApplicationDetails((prv) => ({
      ...prv,
      finalSummeryExp,
    }));
    setAllExpDetails(finalSummeryExp);
  }, [formData]);

  return (
    <div id="experenceInputRemove">
      <Row>
        <h6 className="mb-2 experience-section-heading">Experience Details </h6>
        <hr />
        {formData?.map((data, outerIndex) => (
          <FieldGroup
            allExpDetails={allExpDetails}
            key={outerIndex}
            data={data}
            outerIndex={outerIndex}
            handleInputChange={handleInputChange}
            fieldErrors={fieldErrors}
            style={style}
            checkErrorFn={checkErrorFn}
            addFormEntry={addFormEntry}
            deleteFormEntry={deleteFormEntry}
            addRelevantExperience={addRelevantExperience}
            deleteRelevantExperience={deleteRelevantExperience}
            deleteFieldGroup={deleteFieldGroup}
            postandvacancyDetails={postandvacancyDetails}
            setFieldErrors={setFieldErrors}
            setFormData={setFormData}
            formData={formData}
            applicationDetails={applicationDetails}
            applicationID={applicationID}
            advertisementClosingDate={advertisementClosingDate}
            previousGroupStartDate={
              outerIndex === 0
                ? null
                : formData[outerIndex - 1].startDateOfService
            }
            getMaxEssentialQualificationYear={getMaxEssentialQualificationYear}
            graduationMinDate={getMinExperienceDateFromGraduation()}
          />
        ))}
      </Row>

      {/* <hr/> */}
      <Row xs={12} md={12} className=" mt-2">
        <div className="">
          <h6 className="mb-3">
            <u>All Experience Details</u>
          </h6>
        </div>
        <Col xs={12} md={3}>
          <label className="form-label" style={style.label}>
            <>Total Experience</>
            <br />{" "}
            <span style={{ color: "#921D1E" }}>
              {" "}
              {allExpDetails.total_BaseExp}
            </span>
          </label>
        </Col>
        <Col xs={12} md={3} className="mt-md-0 mt-2">
          <label className="form-label" style={style.label}>
            <> Total Experience(Excluding Trainees)</>
            <br />{" "}
            <span style={{ color: "#921D1E" }}>
              {allExpDetails.total_postWiseTotalExperienceWithoutTrainee}
            </span>{" "}
          </label>
        </Col>
        <Col
          xs={12}
          md={6}
          className="mt-md-0 mt-2 d-flex justify-content-start justify-content-md-end "
        >
          <div>
            <Button
              variant="primary"
              className="btn btn-success"
              onClick={addNewFieldGroup}
            >
              <FaPlus /> Add New Experience
            </Button>
          </div>
        </Col>
      </Row>

      <Row xs={12} md={12} className=" mt-4">
        <Col xs={12} md={3} className="mt-md-0 mt-2">
          <label className="form-label" style={style.label}>
            <>Total Relevant Experience</>
            <br />{" "}
            <span style={{ color: "#921D1E" }}>
              {allExpDetails.total_relevantTotalExperience}
            </span>{" "}
          </label>
        </Col>
        <Col xs={12} md={4} className="mt-md-0 mt-2">
          <label className="form-label" style={style.label}>
            <>Total Relevant Experience(Excluding Trainees)</>
            <br />{" "}
            <span style={{ color: "#921D1E" }}>
              {allExpDetails.total_relevantTotalExperienceWithoutTrainee}
            </span>{" "}
          </label>
        </Col>
      </Row>
    </div>
  );
};

export default ExperienceInputs;
